/* PROGRAM ACCOUNTS, AS THE CONSOLE SHOWS THEM (C9). A program account sees its invitations, its threshold file and one
   verdict per party and version, in the order it invited the parties: the table has no sort and no score column, and
   nothing here orders by one. A party sees which program it is under, and accepts its invitation. Read from
   GET /v1/membership and /v1/program/*; these functions only turn those bodies into what a page draws. */
import type { InvitationStatus, Membership, ProgramParty, ProgramThresholds, ProgramVerdicts, Usage, VerdictRow } from '../types.ts'

const ACCOUNT_ID = /^AC-[A-Za-z0-9]{4,16}$/

/** An invitation is made by account id: the one the party's console shows on its License page. */
export function parseInvite(input: string): { ok: true; id: string } | { ok: false; message: string } {
  const id = input.trim()
  if (!id) return { ok: false, message: 'Name the party by its account id, which starts AC-. The party’s console shows it on the License page.' }
  if (!ACCOUNT_ID.test(id)) return { ok: false, message: `${id.slice(0, 40)} is not an account id. An account id starts AC- and is followed by letters and digits, for example AC-1A2B3C4D5E.` }
  return { ok: true, id }
}

const day = (iso: string | null): string => (iso ? iso.slice(0, 10) : '')
const STATUS: Record<InvitationStatus, { text: string; tone: 'accent' | 'pass' | 'quiet' }> = {
  invited: { text: 'Invited, not yet accepted', tone: 'accent' },
  accepted: { text: 'Accepted', tone: 'pass' },
  revoked: { text: 'Revoked', tone: 'quiet' },
}

export type PartyRow = { id: string; position: number; accountId: string; name: string; status: InvitationStatus; statusText: string; tone: 'accent' | 'pass' | 'quiet'; since: string; canRevoke: boolean }
export type PartiesView = { rows: PartyRow[]; summary: string; empty: boolean }

/** The invitations in the order the server sent them, which is the order they were made. */
export function partiesView(parties: ProgramParty[]): PartiesView {
  const rows = parties.map((p, i) => ({
    id: p.invitation,
    position: i + 1,
    accountId: p.account.id,
    name: p.account.name,
    status: p.status,
    statusText: STATUS[p.status].text,
    tone: STATUS[p.status].tone,
    since: p.status === 'revoked' ? `Revoked ${day(p.revoked)}` : p.status === 'accepted' ? `Accepted ${day(p.accepted)}` : `Invited ${day(p.invited)}`,
    canRevoke: p.status !== 'revoked',
  }))
  const live = rows.filter((r) => r.status !== 'revoked').length
  return { rows, empty: rows.length === 0, summary: rows.length === 0 ? 'Nobody invited yet.' : `${rows.length} ${rows.length === 1 ? 'invitation' : 'invitations'}, ${live} in force, in the order you made them.` }
}

export type ThresholdsView = { present: boolean; title: string; lines: string[]; label: string | null; text: string }
export function thresholdsView(t: ProgramThresholds | null): ThresholdsView {
  if (!t) return { present: false, title: 'No file yet', lines: ['Verdicts are read against the shipped defaults and say so.'], label: 'shipped defaults, unsourced: design numbers, not a standard', text: '' }
  return {
    present: true,
    title: `Version ${t.version}${t.owner ? `, owned by ${t.owner}` : ''}`,
    lines: [t.source ? `Source: ${t.source}` : 'No source named in the file.', `Set ${day(t.updated)}; file hash ${t.file_hash.slice(0, 12)}.`],
    label: t.label,
    text: t.text,
  }
}

/** Whether the file on screen is worth sending: the server reads it with the same parser as `damaros check` and says what is wrong with it. */
export function thresholdsDraft(text: string, current: string): { send: boolean; message: string | null } {
  if (!text.trim()) return { send: false, message: 'Paste the threshold file. `npx damaros check --init` writes one with every rule and its default.' }
  if (text === current) return { send: false, message: null }
  return { send: true, message: null }
}

type Tone = 'pass' | 'fail' | 'quiet' | 'accent'
const STATE: Record<VerdictRow['state'], { text: string; tone: Tone }> = {
  met: { text: 'Met your thresholds', tone: 'pass' },
  not_met: { text: 'Did not meet your thresholds', tone: 'fail' },
  inconclusive: { text: 'Inconclusive at the cases played', tone: 'quiet' },
  in_progress: { text: 'Evaluation run in progress', tone: 'accent' },
  no_evaluation_run: { text: 'No evaluation run yet', tone: 'quiet' },
  not_accepted: { text: 'Invitation not accepted yet', tone: 'quiet' },
}

export type VerdictLine = { key: string; party: string; partyId: string; version: string; stateText: string; tone: Tone; firstFailed: string | null; detail: string | null; run: string | null; label: string | null; revoked: boolean }
export type VerdictsView = { rows: VerdictLine[]; columns: string[]; order: string; meter: string; thresholdsText: string; empty: boolean }

/** The verdicts as the server ordered them: invitation order, then the party's versions as it first tested them. There is no column for a score and nothing is sorted here. */
export function verdictsView(v: ProgramVerdicts): VerdictsView {
  const rows = v.rows.map((r, i) => ({
    key: `${r.invitation.id}:${r.run ?? i}`,
    party: r.party.name,
    partyId: r.party.id,
    version: r.version ? `${r.version.name}@${r.version.version}` : 'None',
    stateText: STATE[r.state].text,
    tone: STATE[r.state].tone,
    firstFailed: r.first_failed,
    detail: r.first_failed_sentence,
    run: r.run,
    label: r.thresholds_label,
    revoked: r.invitation.status === 'revoked',
  }))
  return {
    rows,
    empty: rows.length === 0,
    columns: ['Party', 'Version', 'Verdict', 'First rule that failed'],
    order: 'In the order you invited the parties, then each party’s versions in the order it first tested them. Damaros orders nothing by any score.',
    meter: v.evaluation_versions_allowed === null ? `${v.evaluation_versions_used} versions tested in ${v.quarter} across you and your parties; this account has no license for evaluation runs` : `${v.evaluation_versions_used} of ${v.evaluation_versions_allowed} versions tested in ${v.quarter}, across you and your parties`,
    thresholdsText: v.thresholds.file_hash === null ? `No threshold file yet: ${v.thresholds.label}.` : v.thresholds.label ? `Your file leaves some rules to the defaults: ${v.thresholds.label}.` : 'Your file sets every rule.',
  }
}

export type Held = { id: string; program: string; status: InvitationStatus; statusText: string; tone: 'accent' | 'pass' | 'quiet'; text: string; canAccept: boolean }
export type MembershipView = { isProgram: boolean; held: Held[]; line: string | null }

/** The party's side: each invitation it holds and the one sentence the License page leads with (an accepted invitation first, then an open one, else the latest). */
export function membershipView(m: Membership): MembershipView {
  const held = m.invitations.map((i) => ({
    id: i.invitation,
    program: i.program.name,
    status: i.status,
    statusText: STATUS[i.status].text,
    tone: STATUS[i.status].tone,
    canAccept: i.status === 'invited',
    text:
      i.status === 'accepted'
        ? `Under ${i.program.name}’s license since ${day(i.accepted)}. Your evaluation and train runs are read against its license and count on its meters. ${i.program.name} reads your verdict on each version against its own thresholds file, never a case or a transcript; you read only your own results.`
        : i.status === 'invited'
          ? `${i.program.name} invited you on ${day(i.invited)}. Accept to open evaluation and train runs under its license. It will read your verdict on each version you test against its thresholds file, never a case or a transcript; you read only your own results.`
          : `${i.program.name} revoked this invitation on ${day(i.revoked)}. Your results stay, and no new evaluation or train run opens under its license. Ask ${i.program.name} to invite you again, or ask the operator of this server for a license of your own.`,
  }))
  const shown = held.find((h) => h.status === 'accepted') ?? held.find((h) => h.status === 'invited') ?? held[0] ?? null
  return { isProgram: m.is_program, held, line: shown ? shown.text : null }
}

/** The account's name and id, first on the License page: the id is what a program invites by. */
export function accountLine(u: Usage): string | null {
  return u.account ? `${u.account.name} · ${u.account.id}` : null
}

/** What a program is told above its invitations when its licence cannot open runs: its parties are refused with the same sentence. Nothing when the licence is in force or in grace. */
export function programNotice(state: { inForce: boolean; status: string; sentence: string } | null): { tone: 'review'; text: string } | null {
  if (!state || (state.inForce && state.status !== 'grace')) return null
  return { tone: 'review', text: state.inForce ? `${state.sentence} Your parties’ evaluation and train runs open until grace ends.` : `Your parties cannot open evaluation or train runs: ${state.sentence}` }
}

/** The program view is offered only to a program account: it is one more mnemonic and one more link, and no other account sees it. */
export const showProgram = (m: Membership | null): boolean => m?.is_program === true
